import { inject, Injectable } from '@angular/core';
import { ButtonItem } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToggleReportSortDirection, ToggleReportSortOption } from 'src/app/store/actions';
import { getReportSort } from 'src/app/store/selectors';

import { ReportSortButtonAction, ReportSorterRequest, ReportSortOption } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ReportSortService {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);

  reportSort$ = this.store.pipe(select(getReportSort));

  sort<T>(request: ReportSorterRequest<T>): T[] {
    return [...request.elements].sort((a, b) => {
      let textA = '';
      let textB = '';
      switch (request.option) {
      case ReportSortOption.sortCropFieldName:
        textA = `${request.cropFieldNameFunc(a)} ${request.cropNameFunc(a)}`;
        textB = `${request.cropFieldNameFunc(b)} ${request.cropNameFunc(b)}}`;
        break;
      case ReportSortOption.sortCropName:
        textA = `${request.cropNameFunc(a)} ${request.cropFieldNameFunc(a)}`;
        textB = `${request.cropNameFunc(b)} ${request.cropFieldNameFunc(b)}`;
        break;
      }
      const lesser = request.descending ? 1 : -1;
      const greater = request.descending ? -1 : 1;
      return textA.toLowerCase() < textB.toLowerCase() ? lesser : greater;
    });
  }

  getButtonItems(): Observable<ButtonItem[]> {
    return this.reportSort$.pipe(
      map((s) => [
        {
          icon: 'sort',
          viewValue: this.locale.translate(s.option),
          key: ReportSortButtonAction.sortOptions,
        },
        {
          icon: s.descending ? 'keyboard_arrow_down' : 'keyboard_arrow_up',
          viewValue: '',
          key: ReportSortButtonAction.sortDirection,
        },
      ]),
    );
  }

  handleButtonAction(data: string) {
    switch (data) {
    case ReportSortButtonAction.sortOptions:
      this.store.dispatch(new ToggleReportSortOption());
      break;
    case ReportSortButtonAction.sortDirection:
      this.store.dispatch(new ToggleReportSortDirection());
      break;
    }
  }
}
